/**
 * A project's place in the setup flow, kept in the project's `status` in the
 * project service (the same values FRaiME uses), plus what the project has
 * (custom fields, documents, outputs). Pure, so the stepper's rules are tested.
 */
export const PROJECT_STATUS = {
  DocumentUpload: "Document Upload",
  EvidenceBase: "Evidence Base",
  InProgress: "In Progress",
  Complete: "Complete",
} as const;

export const STEP_IDS = ["fields", "upload", "evidence", "outputs"] as const;
export type StepId = (typeof STEP_IDS)[number];

export const STEP_LABELS: Record<StepId, string> = {
  fields: "Custom Fields",
  upload: "Upload Files",
  evidence: "Evidence Base",
  outputs: "Outputs",
};

export interface ProjectProgress {
  status: string | null | undefined;
  hasCustomFields: boolean;
  hasDocuments: boolean;
  hasOutputs: boolean;
}

const isPastEvidenceBase = (status: ProjectProgress["status"]) => status === PROJECT_STATUS.InProgress || status === PROJECT_STATUS.Complete;

/** Where the project opens: the furthest step reached. */
export function startingStep({ status, hasCustomFields, hasDocuments, hasOutputs }: ProjectProgress): StepId {
  if (isPastEvidenceBase(status)) return hasOutputs ? "outputs" : "evidence";
  if (status === PROJECT_STATUS.EvidenceBase || hasDocuments) return "evidence";
  if (hasCustomFields) return "upload";
  return "fields";
}

/** Steps shown as done (ticked). */
export function completedSteps({ status, hasCustomFields, hasDocuments, hasOutputs }: ProjectProgress): StepId[] {
  const done: StepId[] = [];
  if (hasCustomFields) done.push("fields");
  if (hasDocuments || status === PROJECT_STATUS.EvidenceBase || isPastEvidenceBase(status)) done.push("upload");
  if (isPastEvidenceBase(status)) done.push("evidence");
  if (hasOutputs) done.push("outputs");
  return done;
}

/**
 * Steps that can be clicked: everything up to the furthest step reached (by the
 * project's progress, or in this visit). Outputs need documents.
 */
export function reachableSteps(progress: ProjectProgress, furthestVisited: StepId): StepId[] {
  const furthest = Math.max(STEP_IDS.indexOf(startingStep(progress)), STEP_IDS.indexOf(furthestVisited));
  return STEP_IDS.filter((step, index) => index <= furthest && (progress.hasDocuments || step !== "outputs"));
}

/** The status to record when the user moves on from `step` (null: leave it). Only ever moves forward. */
export function statusAfter(step: StepId, current: ProjectProgress["status"]): string | null {
  if (step === "upload" && (!current || current === PROJECT_STATUS.DocumentUpload)) return PROJECT_STATUS.EvidenceBase;
  if (step === "evidence" && !isPastEvidenceBase(current)) return PROJECT_STATUS.InProgress;
  return null;
}

export const isStepId = (value: string | null): value is StepId => !!value && (STEP_IDS as readonly string[]).includes(value);

/** Chat was a step before it became the workspace's floating assistant: old `?step=chat` links open the Evidence Base. */
export const normaliseStepParam = (value: string | null): string | null => (value === "chat" ? "evidence" : value);
